// Reading tokens: decoding the JWT payload (no signature check -- see
// verify.ts), expiry, and where the current token comes from.

import { getSessionConfig, ResolvedSessionConfig } from "./config";
import { cookies } from "./cookies";

export type TokenClaims = {
	sub?: string;
	email?: string;
	name?: string;
	exp?: number;
	iat?: number;
	auth_time?: number;
	token_use?: string;
	[claim: string]: unknown;
};

/** The JWT payload, or null when the token is missing or unreadable. Does not verify. */
export function decodeToken(token: string | null | undefined): TokenClaims | null {
	if (!token) return null;
	try {
		const part = token.split(".")[1];
		if (!part) return null;
		const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
		const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
		const binary = atob(padded);
		// UTF-8 claims (names with accents) need the bytes decoded, not the latin-1 string
		let json: string;
		try {
			json = decodeURIComponent(
				binary
					.split("")
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join("")
			);
		} catch {
			json = binary;
		}
		const payload = JSON.parse(json);
		return payload && typeof payload === "object" ? (payload as TokenClaims) : null;
	} catch {
		return null;
	}
}

/** The token's exp in ms, or null when it has none / can't be read. */
export function tokenExpiryMs(token: string | null | undefined): number | null {
	const exp = decodeToken(token)?.exp;
	return typeof exp === "number" ? exp * 1000 : null;
}

/**
 * True when the token is missing, unreadable, has no exp, or expires within
 * `skewSec` seconds (default 60) of `now`.
 */
export function isExpired(token: string | null | undefined, skewSec = 60, now: number = Date.now()): boolean {
	const expiry = tokenExpiryMs(token);
	if (expiry === null) return true;
	return expiry - skewSec * 1000 <= now;
}

export type ResolvedToken = {
	token: string | null;
	source: "query" | "cookie" | null;
	/** the token came from ?token= (a hand-off from the sign-in app or another app) */
	freshLogin: boolean;
};

const queryToken = (): string | null => {
	try {
		return new URLSearchParams(window.location.search).get("token") || null;
	} catch {
		return null;
	}
};

/** The current ID token cookie (what API calls should send). */
export const getToken = (config?: ResolvedSessionConfig): string | null => cookies.getIdToken(config) ?? null;

/**
 * Where this page's token comes from: `?token=` wins over the cookie (a
 * hand-off is newer than whatever an earlier session left on the domain) and,
 * when it is still valid, is written to the cookie so API calls use it. With
 * `strip` (default) the token is then removed from the address bar.
 */
export function resolveToken(options: { strip?: boolean; config?: ResolvedSessionConfig } = {}): ResolvedToken {
	const config = options.config ?? getSessionConfig();
	const fromQuery = queryToken();
	if (fromQuery) {
		if (!isExpired(fromQuery, 0)) cookies.setIdToken(fromQuery, config);
		if (options.strip !== false) stripTokenFromUrl();
		return { token: fromQuery, source: "query", freshLogin: true };
	}
	const fromCookie = getToken(config);
	if (fromCookie) return { token: fromCookie, source: "cookie", freshLogin: false };
	return { token: null, source: null, freshLogin: false };
}

/** Removes ?token= from the address bar (history.replaceState; other params and the hash stay). */
export function stripTokenFromUrl(): void {
	try {
		const url = new URL(window.location.href);
		if (!url.searchParams.has("token")) return;
		url.searchParams.delete("token");
		window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
	} catch {
		// no history API (or an unparsable URL): leave the address bar as is
	}
}

/** The current URL without its ?token= parameter. */
export function currentUrlWithoutToken(): string {
	try {
		const url = new URL(window.location.href);
		url.searchParams.delete("token");
		return url.href;
	} catch {
		return `${window.location.origin}${window.location.pathname}`;
	}
}

/**
 * `url` with ?token=<token> set (replacing any earlier one, keeping other
 * params and the hash). Used to hand the session to another app.
 */
export function withTokenParam(url: string, token: string): string {
	try {
		const parsed = new URL(url);
		parsed.searchParams.set("token", token);
		return parsed.href;
	} catch {
		const separator = url.includes("?") ? "&" : "?";
		return `${url}${separator}token=${encodeURIComponent(token)}`;
	}
}
